/* eslint-disable react/no-multi-comp */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
    Button,
    Empty,
    Input,
    Skeleton,
    Space,
    Tag,
    Tooltip,
    Typography,
} from '@app/ui';
import { SearchOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';

import sportsApi from '@app/utils/sportsApi';
import connect from '@app/utils/connect';
import { User } from '@app/types/api';
import { API_LIVE_DOCS } from '@app/constants/config';
import reloEdgeFull from '@app/images/relo_edge_full.svg';

import { OpenApiSpec } from './types';
import { methodColor } from './openApiUtils';
import {
    ApiCategory,
    RefEndpoint,
    RefSchema,
    parseApiReference,
} from './apiReferenceModel';
import { buildSnippet, SnippetLang } from './buildSnippet';
import TryItConsole from './TryItConsole';
import './ApiReference.css';

const { Title, Text, Paragraph } = Typography;

type ApiReferenceProps = {
    user: User;
};

type FetchState = 'loading' | 'success' | 'forbidden' | 'error';

const LANG_KEY = 'apiReference.lang';
const NAV_WIDTH_KEY = 'apiReference.navWidth';
const NAV_WIDTH_MIN = 180;
const NAV_WIDTH_MAX = 480;
const NAV_WIDTH_DEFAULT = 256;
/** Keyboard nudge step for the resizer (ArrowLeft / ArrowRight). */
const NAV_WIDTH_STEP = 16;

const clampNavWidth = (w: number) =>
    Math.min(NAV_WIDTH_MAX, Math.max(NAV_WIDTH_MIN, w));

const LANGS: Array<{ id: SnippetLang; label: string }> = [
    { id: 'curl', label: 'cURL' },
    { id: 'python', label: 'Python' },
    { id: 'node', label: 'Node' },
];

/**
 * Base URL shown in the copy-pasteable code snippets and the static auth block.
 * Per the Client Integration API doc this is always the prod host — independent
 * of the env the dashboard is running against. The Try-It console still
 * EXECUTES against the env-resolved `sportsApiUrl`; only the displayed snippets
 * use this constant.
 */
const DOC_BASE_URL = 'https://sports-api.prod.rmdevops.com';

const CATEGORY_TAG_COLOR: Record<ApiCategory, string> = {
    Dimension: 'purple',
    Data: 'blue',
    Collections: 'cyan',
};

/** Maps a model category value to its i18n key under apiReference.categories. */
const CATEGORY_I18N_KEY: Record<ApiCategory, string> = {
    Dimension: 'dimension',
    Data: 'data',
    Collections: 'collections',
};

/** A small copy-to-clipboard button rendered over a code block. */
const CopyButton = ({ text }: { text: string }) => {
    const { t } = useTranslation('settings');
    const [copied, setCopied] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout>>();

    useEffect(
        () => () => {
            if (timer.current) clearTimeout(timer.current);
        },
        [],
    );

    const onCopy = () => {
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard
                .writeText(text)
                .then(() => {
                    setCopied(true);
                    if (timer.current) clearTimeout(timer.current);
                    timer.current = setTimeout(() => setCopied(false), 1500);
                })
                .catch(() => undefined);
        }
    };

    const label = copied
        ? t('userSettings.apiReference.copied')
        : t('userSettings.apiReference.copyToClipboard');

    return (
        <Tooltip title={label} open={copied || undefined}>
            <Button
                className="api-ref__copy"
                data-test="api-ref-copy"
                type="link"
                size="small"
                icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                onClick={onCopy}
                aria-label={label}
            />
        </Tooltip>
    );
};

/** A light, mono code panel matching the Try-It response block. */
const CodeBlock = ({
    label,
    code,
}: {
    label: string;
    code: string;
}) => (
    <div className="api-ref__code-block" data-test="api-ref-code">
        <span className="api-ref__code-label">{label}</span>
        <CopyButton text={code} />
        <pre className="api-ref-code__pre">{code}</pre>
    </div>
);

const ApiReference = ({ user }: ApiReferenceProps) => {
    const { t } = useTranslation('settings');
    const navigate = useNavigate();
    const { clientId, operationId, schemaName } = useParams<{
        clientId: string;
        operationId?: string;
        schemaName?: string;
    }>();

    const client = useMemo(
        () =>
            user?.clientsById && user?.selectedClientId
                ? user.clientsById[Number(user.selectedClientId)]
                : null,
        [user],
    );

    const [query, setQuery] = useState('');
    const [lang, setLang] = useState<SnippetLang>(() => {
        const saved =
            typeof localStorage !== 'undefined'
                ? localStorage.getItem(LANG_KEY)
                : null;
        return (LANGS.find(l => l.id === saved)?.id ?? 'curl') as SnippetLang;
    });

    useEffect(() => {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(LANG_KEY, lang);
        }
    }, [lang]);

    // --- resizable left nav --------------------------------------------------

    const [navWidth, setNavWidth] = useState<number>(() => {
        const saved =
            typeof localStorage !== 'undefined'
                ? Number(localStorage.getItem(NAV_WIDTH_KEY))
                : NaN;
        return Number.isFinite(saved) && saved > 0
            ? clampNavWidth(saved)
            : NAV_WIDTH_DEFAULT;
    });

    useEffect(() => {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(NAV_WIDTH_KEY, String(navWidth));
        }
    }, [navWidth]);

    const gridRef = useRef<HTMLDivElement>(null);

    const onResizerPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const onResizerPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        // Only resize while the pointer is captured (i.e. mid-drag).
        if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
        const rect = gridRef.current?.getBoundingClientRect();
        if (!rect) return;
        setNavWidth(clampNavWidth(e.clientX - rect.left));
    };
    const onResizerPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
    };
    const onResizerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            setNavWidth(w => clampNavWidth(w - NAV_WIDTH_STEP));
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            setNavWidth(w => clampNavWidth(w + NAV_WIDTH_STEP));
        }
    };

    // Spec fetch via react-query so navigating away and back re-uses the cached
    // spec instead of refetching. The app provides a shared QueryClientProvider
    // at its root (src/utils/queryClient.ts), verified present.
    const {
        data: spec = null,
        isLoading,
        error: queryError,
    } = useQuery<OpenApiSpec, { status?: number }>(
        ['apiReference', 'openapi.json'],
        () => sportsApi.url('/openapi.json').get().json() as Promise<OpenApiSpec>,
        {
            staleTime: Infinity,
            cacheTime: Infinity,
            retry: false,
            refetchOnWindowFocus: false,
        },
    );

    const state: FetchState = isLoading
        ? 'loading'
        : queryError
        ? queryError.status === 403
            ? 'forbidden'
            : 'error'
        : 'success';

    const model = useMemo(() => parseApiReference(spec), [spec]);

    // Endpoints grouped by raw tag (preserving first-seen order).
    const endpointGroups = useMemo(() => {
        const map = new Map<string, RefEndpoint[]>();
        model.endpoints.forEach(e => {
            const bucket = map.get(e.group) ?? [];
            bucket.push(e);
            map.set(e.group, bucket);
        });
        return Array.from(map.entries());
    }, [model.endpoints]);

    // Schemas grouped by their heuristic group.
    const schemaGroups = useMemo(() => {
        const map = new Map<string, RefSchema[]>();
        model.schemas.forEach(s => {
            const bucket = map.get(s.group) ?? [];
            bucket.push(s);
            map.set(s.group, bucket);
        });
        return Array.from(map.entries());
    }, [model.schemas]);

    const selectedEndpoint = useMemo(
        () =>
            operationId
                ? model.endpoints.find(e => e.operationId === operationId)
                : undefined,
        [model.endpoints, operationId],
    );
    const selectedSchema = useMemo(
        () =>
            schemaName
                ? model.schemas.find(s => s.name === schemaName)
                : undefined,
        [model.schemas, schemaName],
    );

    const view: 'overview' | 'endpoint' | 'schema' = operationId
        ? 'endpoint'
        : schemaName
        ? 'schema'
        : 'overview';

    // --- a11y: move focus to the view heading on route change ----------------

    const headingRef = useRef<HTMLHeadingElement>(null);
    useEffect(() => {
        // Only steal focus once the spec has loaded and the heading exists.
        if (headingRef.current) {
            headingRef.current.focus();
        }
    }, [view, operationId, schemaName]);

    // --- document.title per view ---------------------------------------------

    useEffect(() => {
        const base = spec?.info?.title ?? 'API Reference';
        let title = base;
        if (view === 'endpoint' && operationId) {
            title = `${operationId} · ${base}`;
        } else if (view === 'schema' && schemaName) {
            title = `${schemaName} · ${base}`;
        }
        const prev = document.title;
        document.title = title;
        return () => {
            document.title = prev;
        };
    }, [view, operationId, schemaName, spec]);

    // --- gating + load states ------------------------------------------------

    if (client && !client.permissions?.includes(API_LIVE_DOCS)) {
        return <Navigate to={`/c/${clientId ?? client.id}`} replace />;
    }

    if (state === 'loading') {
        return (
            <div className="api-ref" data-test="api-ref">
                <div className="api-ref__pane api-ref__nav">
                    <Skeleton
                        active
                        title={false}
                        paragraph={{ rows: 8 }}
                    />
                </div>
                <div className="api-ref__pane api-ref__center">
                    <Skeleton active paragraph={{ rows: 6 }} />
                </div>
                <div className="api-ref__pane api-ref__code">
                    <Skeleton active paragraph={{ rows: 6 }} />
                </div>
            </div>
        );
    }

    if (state === 'forbidden' || state === 'error') {
        return (
            <div className="api-ref" data-test="api-ref">
                <div
                    style={{
                        gridColumn: '1 / -1',
                        padding: 48,
                    }}
                >
                    <Empty
                        description={
                            state === 'forbidden'
                                ? t('userSettings.apiReference.forbidden')
                                : t('userSettings.apiReference.loadError')
                        }
                    />
                </div>
            </div>
        );
    }

    // --- nav helpers ---------------------------------------------------------

    const cid = clientId ?? String(client?.id ?? '');
    const goOverview = () => navigate(`/c/${cid}/api-documentation`);
    const goEndpoint = (id: string) =>
        navigate(`/c/${cid}/api-documentation/operations/${id}`);
    const goSchema = (name: string) =>
        navigate(`/c/${cid}/api-documentation/schemas/${name}`);

    const q = query.trim().toLowerCase();
    const matchEndpoint = (e: RefEndpoint) =>
        !q ||
        [e.summary ?? '', e.path, e.operationId, e.groupLabel]
            .join(' ')
            .toLowerCase()
            .includes(q);
    const matchSchema = (s: RefSchema) =>
        !q || s.name.toLowerCase().includes(q);

    const visibleEndpointGroups = endpointGroups
        .map(([tag, eps]) => [tag, eps.filter(matchEndpoint)] as const)
        .filter(([, eps]) => eps.length > 0);
    const visibleSchemaGroups = schemaGroups
        .map(([g, ss]) => [g, ss.filter(matchSchema)] as const)
        .filter(([, ss]) => ss.length > 0);

    const clientName =
        client?.name ?? t('userSettings.apiReference.thisClient');

    // --- left nav ------------------------------------------------------------

    const nav = (
        <nav
            className="api-ref__pane api-ref__nav"
            data-test="api-ref-nav"
        >
            <a
                className="api-ref__back"
                onClick={e => {
                    e.preventDefault();
                    navigate(`/c/${cid}`);
                }}
                href={`/c/${cid}`}
            >
                {t('userSettings.apiReference.back')}
            </a>

            <div className="api-ref__brand">
                <Space size={8} align="center">
                    <img
                        className="api-ref__brand-logo"
                        src={reloEdgeFull}
                        alt="Relo EDGE"
                    />
                    <Tag>API v1</Tag>
                </Space>
            </div>

            <Input
                className="api-ref__search"
                data-test="api-ref-search"
                allowClear
                size="small"
                prefix={<SearchOutlined />}
                placeholder={t('userSettings.apiReference.search')}
                value={query}
                onChange={e => setQuery(e.target.value)}
            />

            <div className="api-ref__nav-group">
                <div className="api-ref__nav-grouphead">
                    <span className="api-ref__nav-grouptitle">
                        {t('userSettings.apiReference.gettingStarted')}
                    </span>
                </div>
                <button
                    type="button"
                    className={`api-ref__nav-link${
                        view === 'overview' ? ' is-active' : ''
                    }`}
                    aria-current={view === 'overview' ? 'page' : undefined}
                    onClick={goOverview}
                >
                    <span className="api-ref__nav-label">
                        {t('userSettings.apiReference.introduction')}
                    </span>
                </button>
            </div>

            {visibleEndpointGroups.map(([tag, eps]) => {
                const first = eps[0];
                return (
                    <div className="api-ref__nav-group" key={tag}>
                        <div className="api-ref__nav-grouphead">
                            <span className="api-ref__nav-grouptitle">
                                {first.groupLabel}
                            </span>
                            <Tag
                                color={CATEGORY_TAG_COLOR[first.category]}
                                style={{ marginInlineStart: 0 }}
                            >
                                {t(
                                    `userSettings.apiReference.categories.${
                                        CATEGORY_I18N_KEY[first.category]
                                    }`,
                                )}
                            </Tag>
                        </div>
                        {eps.map(e => (
                            <button
                                type="button"
                                key={e.operationId}
                                className={`api-ref__nav-link${
                                    operationId === e.operationId
                                        ? ' is-active'
                                        : ''
                                }`}
                                aria-current={
                                    operationId === e.operationId
                                        ? 'page'
                                        : undefined
                                }
                                onClick={() => goEndpoint(e.operationId)}
                                title={e.path}
                            >
                                <Tag
                                    color={methodColor(e.method)}
                                    className="api-ref__nav-method"
                                >
                                    {e.method}
                                </Tag>
                                <span className="api-ref__nav-label">
                                    {e.summary || e.path}
                                </span>
                            </button>
                        ))}
                    </div>
                );
            })}

            {visibleSchemaGroups.map(([group, schemas]) => (
                <div className="api-ref__nav-group" key={`schema-${group}`}>
                    <div className="api-ref__nav-grouphead">
                        <span className="api-ref__nav-grouptitle">{group}</span>
                    </div>
                    {schemas.map(s => (
                        <button
                            type="button"
                            key={s.name}
                            className={`api-ref__nav-link${
                                schemaName === s.name ? ' is-active' : ''
                            }`}
                            aria-current={
                                schemaName === s.name ? 'page' : undefined
                            }
                            onClick={() => goSchema(s.name)}
                        >
                            <span className="api-ref__nav-label">{s.name}</span>
                        </button>
                    ))}
                </div>
            ))}
        </nav>
    );

    // --- center pane ---------------------------------------------------------

    const overviewCenter = (
        <>
            <h2
                ref={headingRef}
                tabIndex={-1}
                className="api-ref__view-heading"
                data-test="api-ref-heading"
            >
                {spec?.info?.title ??
                    t('userSettings.apiReference.defaultTitle')}
            </h2>
            {spec?.info?.description ? (
                <Paragraph type="secondary">
                    {spec.info.description}
                </Paragraph>
            ) : (
                <Paragraph type="secondary">
                    {t('userSettings.apiReference.defaultDescription')}
                </Paragraph>
            )}

            <Title level={5} className="api-ref__section-title">
                {t('userSettings.apiReference.authentication')}
            </Title>
            <Paragraph type="secondary">
                {t('userSettings.apiReference.authIntro')}
            </Paragraph>
            <div className="api-ref__auth-block">
                <pre className="api-ref-code__pre">
                    {'Authorization: Basic <base64(login:password)>\n' +
                        'api-consumer-key: client-integration'}
                </pre>
            </div>
            <Paragraph type="secondary">
                {t('userSettings.apiReference.authTokenNote')}
            </Paragraph>

            <Title level={5} className="api-ref__section-title">
                {t('userSettings.apiReference.conventions')}
            </Title>
            <div className="api-ref__convention-block">
                <dl className="api-ref__dl">
                    <dt>{t('userSettings.apiReference.baseUrl')}</dt>
                    <dd>
                        <Text code>{`${DOC_BASE_URL}/v1`}</Text>
                    </dd>
                    <dt>{t('userSettings.apiReference.format')}</dt>
                    <dd>{t('userSettings.apiReference.formatValue')}</dd>
                    <dt>{t('userSettings.apiReference.errors')}</dt>
                    <dd>
                        {t('userSettings.apiReference.errorsBody')}{' '}
                        <button
                            type="button"
                            className="api-ref__link"
                            onClick={() => goSchema('Error')}
                        >
                            Error
                        </button>{' '}
                        {t('userSettings.apiReference.errorsBodySuffix')}
                    </dd>
                </dl>
            </div>

            <div className="api-ref__cards">
                {(['Dimension', 'Data', 'Collections'] as ApiCategory[]).map(
                    cat => {
                        const count = model.endpoints.filter(
                            e => e.category === cat,
                        ).length;
                        return (
                            <div
                                key={cat}
                                style={{
                                    border:
                                        '1px solid var(--border, #e8e8e8)',
                                    borderRadius: 6,
                                    padding: 12,
                                }}
                            >
                                <Tag color={CATEGORY_TAG_COLOR[cat]}>
                                    {t(
                                        `userSettings.apiReference.categories.${CATEGORY_I18N_KEY[cat]}`,
                                    )}
                                </Tag>
                                <div>
                                    <Text type="secondary">
                                        {t(
                                            'userSettings.apiReference.endpointCount',
                                            { count },
                                        )}
                                    </Text>
                                </div>
                            </div>
                        );
                    },
                )}
            </div>
        </>
    );

    const endpointCenter = selectedEndpoint ? (
        <>
            <h2
                ref={headingRef}
                tabIndex={-1}
                className="api-ref__view-heading api-ref__view-heading--sr"
                data-test="api-ref-heading"
            >
                {selectedEndpoint.method.toUpperCase()} {selectedEndpoint.path}
            </h2>
            <div className="api-ref__breadcrumb">
                <Text type="secondary">
                    {selectedEndpoint.groupLabel}
                    {selectedEndpoint.summary
                        ? ` / ${selectedEndpoint.summary}`
                        : ''}
                </Text>
            </div>
            <div className="api-ref__op-line">
                <Tag color={methodColor(selectedEndpoint.method)}>
                    {selectedEndpoint.method.toUpperCase()}
                </Tag>
                <Text className="api-ref__path">{selectedEndpoint.path}</Text>
            </div>
            {selectedEndpoint.summary ? (
                <Title level={4} style={{ marginTop: 4 }}>
                    {selectedEndpoint.summary}
                </Title>
            ) : null}
            {selectedEndpoint.description ? (
                <Paragraph type="secondary">
                    {selectedEndpoint.description}
                </Paragraph>
            ) : null}

            <TryItConsole
                endpoint={selectedEndpoint}
                clientName={clientName}
            />

            <Title level={5} className="api-ref__section-title">
                {t('userSettings.apiReference.parameters')}
            </Title>
            {selectedEndpoint.params.length === 0 ? (
                <Text type="secondary">
                    {t('userSettings.apiReference.noParameters')}
                </Text>
            ) : (
                <table className="api-ref__table">
                    <thead>
                        <tr>
                            <th scope="col">
                                {t('userSettings.apiReference.name')}
                            </th>
                            <th scope="col">
                                {t('userSettings.apiReference.type')}
                            </th>
                            <th scope="col">
                                {t('userSettings.apiReference.description')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {selectedEndpoint.params.map(p => (
                            <tr key={`${p.in}:${p.name}`}>
                                <td>
                                    <Space size={4} wrap>
                                        <Text code>{p.name}</Text>
                                        <Tag>{p.loc}</Tag>
                                        {p.required ? (
                                            <Tag color="red">
                                                {t(
                                                    'userSettings.apiDocumentation.required',
                                                )}
                                            </Tag>
                                        ) : null}
                                    </Space>
                                </td>
                                <td>
                                    <Text type="secondary">{p.type}</Text>
                                </td>
                                <td>
                                    <Text type="secondary">
                                        {p.description ?? ''}
                                    </Text>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <Title level={5} className="api-ref__section-title">
                {t('userSettings.apiReference.responses')}
            </Title>
            <table className="api-ref__table">
                <thead>
                    <tr>
                        <th scope="col">
                            {t('userSettings.apiReference.status')}
                        </th>
                        <th scope="col">
                            {t('userSettings.apiReference.schema')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <Tag color="green">200</Tag>
                        </td>
                        <td>
                            {selectedEndpoint.returns ? (
                                <button
                                    type="button"
                                    className="api-ref__link"
                                    onClick={() =>
                                        goSchema(selectedEndpoint.returns || '')
                                    }
                                >
                                    {selectedEndpoint.returns}
                                </button>
                            ) : (
                                <Text type="secondary">—</Text>
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <Tag className="api-ref-tag--danger">error</Tag>
                        </td>
                        <td>
                            <button
                                type="button"
                                className="api-ref__link"
                                onClick={() => goSchema('Error')}
                            >
                                Error
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </>
    ) : (
        <Empty
            description={t('userSettings.apiReference.endpointNotFound')}
        />
    );

    const schemaCenter = selectedSchema ? (
        <>
            <h2
                ref={headingRef}
                tabIndex={-1}
                className="api-ref__view-heading api-ref__view-heading--sr"
                data-test="api-ref-heading"
            >
                {selectedSchema.name}
            </h2>
            <div className="api-ref__op-line">
                <Text className="api-ref__path">{selectedSchema.name}</Text>
            </div>
            {selectedSchema.description ? (
                <Paragraph type="secondary">
                    {selectedSchema.description}
                </Paragraph>
            ) : null}
            <Title level={5} className="api-ref__section-title">
                {t('userSettings.apiReference.fields')}
            </Title>
            {selectedSchema.fields.length === 0 ? (
                <Text type="secondary">
                    {t('userSettings.apiReference.noFields')}
                </Text>
            ) : (
                <table className="api-ref__table">
                    <thead>
                        <tr>
                            <th scope="col">
                                {t('userSettings.apiReference.field')}
                            </th>
                            <th scope="col">
                                {t('userSettings.apiReference.type')}
                            </th>
                            <th scope="col">
                                {t('userSettings.apiReference.description')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {selectedSchema.fields.map(f => (
                            <tr key={f.name}>
                                <td>
                                    <Space size={4} wrap>
                                        <Text code>{f.name}</Text>
                                        {f.required ? (
                                            <Tag color="red">
                                                {t(
                                                    'userSettings.apiDocumentation.required',
                                                )}
                                            </Tag>
                                        ) : null}
                                    </Space>
                                </td>
                                <td>
                                    {f.ref ? (
                                        <button
                                            type="button"
                                            className="api-ref__link"
                                            onClick={() => goSchema(f.ref || '')}
                                        >
                                            {f.type}
                                        </button>
                                    ) : (
                                        <Text type="secondary">{f.type}</Text>
                                    )}
                                </td>
                                <td>
                                    <Text type="secondary">
                                        {f.description ?? ''}
                                    </Text>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </>
    ) : (
        <Empty description={t('userSettings.apiReference.schemaNotFound')} />
    );

    const center = (
        <div className="api-ref__pane api-ref__center">
            {view === 'overview'
                ? overviewCenter
                : view === 'endpoint'
                ? endpointCenter
                : schemaCenter}
        </div>
    );

    // --- right pane (code) ---------------------------------------------------

    const langTabs = (
        <div className="api-ref__lang-tabs">
            {LANGS.map(l => (
                <Button
                    key={l.id}
                    size="small"
                    variant={lang === l.id ? 'primary' : undefined}
                    type={lang === l.id ? undefined : 'link'}
                    onClick={() => setLang(l.id)}
                >
                    {l.label}
                </Button>
            ))}
        </div>
    );

    let rightBody: React.ReactNode;
    if (view === 'endpoint' && selectedEndpoint) {
        const snippet = buildSnippet(
            selectedEndpoint,
            lang,
            `${DOC_BASE_URL}/v1`,
        );
        // Real response example: the synthesized example of the returned
        // schema, looked up from the already-parsed model.
        const responseExample = selectedEndpoint.returns
            ? model.schemas.find(s => s.name === selectedEndpoint.returns)
                  ?.example
            : undefined;
        rightBody = (
            <>
                {langTabs}
                <CodeBlock
                    label={t('userSettings.apiReference.request')}
                    code={snippet}
                />
                {selectedEndpoint.requestBodyExample !== undefined ? (
                    <CodeBlock
                        label={t('userSettings.apiReference.requestBody')}
                        code={JSON.stringify(
                            selectedEndpoint.requestBodyExample,
                            null,
                            2,
                        )}
                    />
                ) : null}
                <CodeBlock
                    label={t('userSettings.apiReference.responseStatus')}
                    code={JSON.stringify(
                        responseExample ?? { ok: true },
                        null,
                        2,
                    )}
                />
            </>
        );
    } else if (view === 'schema' && selectedSchema) {
        rightBody = (
            <CodeBlock
                label={t('userSettings.apiReference.example')}
                code={JSON.stringify(selectedSchema.example, null, 2)}
            />
        );
    } else {
        rightBody = (
            <>
                {langTabs}
                <CodeBlock
                    label={t('userSettings.apiReference.authentication')}
                    code={
                        'curl "' +
                        DOC_BASE_URL +
                        '/v1/..." \\\n' +
                        '  -H "Authorization: Basic $CREDENTIALS" \\\n' +
                        '  -H "api-consumer-key: client-integration"'
                    }
                />
            </>
        );
    }

    const right = (
        <div className="api-ref__pane api-ref__code">{rightBody}</div>
    );

    return (
        <div
            ref={gridRef}
            className="api-ref"
            data-test="api-ref"
            style={{
                gridTemplateColumns: `${navWidth}px minmax(360px, 1fr) 416px`,
            }}
        >
            {nav}
            <div
                className="api-ref__resizer"
                data-test="api-ref-resizer"
                role="separator"
                aria-orientation="vertical"
                // ponytail: static aria-label — the followup scope forbids new
                // i18n keys, and there's no existing key for "resize navigation".
                aria-label="Resize navigation"
                aria-valuenow={navWidth}
                aria-valuemin={NAV_WIDTH_MIN}
                aria-valuemax={NAV_WIDTH_MAX}
                tabIndex={0}
                style={{ left: navWidth }}
                onPointerDown={onResizerPointerDown}
                onPointerMove={onResizerPointerMove}
                onPointerUp={onResizerPointerUp}
                onKeyDown={onResizerKeyDown}
            />
            {center}
            {right}
        </div>
    );
};

export default connect(['clients', 'user'])(ApiReference);
